import {
  faAngleDown,
  faDownload,
  faPaste,
  faShareNodes,
  faSignOut,
} from "@fortawesome/pro-regular-svg-icons";
import { faCheck, faSpinner } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { useState } from "react";
import Blockies from "react-18-blockies";
import { useDetectClickOutside } from "react-detect-click-outside";
import { Link, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import useLocalStorageState from "use-local-storage-state";

import { getProjectPages } from "@/hooks/useQueries/pages";

import { projectMap } from "../../constants";
import useCeramic from "../../hooks/useCeramic";
import useQueries from "../../hooks/useQueries";
import PublicLink from "../PublicLink";
import styles from "./Header.module.scss";

export default function Header() {
  let { projectId = "global-graph", projectPublicId } = useParams();
  const isReadOnly = Boolean(projectPublicId);
  const { address, logout } = useCeramic();
  const { currentProject } = useQueries();

  const [isExporting, setIsExporting] = useState<boolean>(false);

  const download = async () => {
    setIsExporting(true);
    const abortController = new AbortController();
    const pages = await getProjectPages({
      signal: abortController.signal,
      queryKey: ["", currentProject?.id as string],
    });
    const exportData = {
      project: currentProject,
      pages,
    };

    const text = JSON.stringify(exportData);
    const element = document.createElement("a");
    element.setAttribute("href", "data:application/json;charset=utf-8," + encodeURIComponent(text));
    element.setAttribute(
      "download",
      `coordination-${currentProject?.slug}-${new Date().toJSON().slice(0, 10)}.json`
    );
    element.style.display = "none";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    setIsExporting(false);
  };

  const [publicLinkVisible, setPublicLinkVisible] = useState<boolean>(false);

  const togglePublicLink = () => {
    setPublicLinkVisible(!publicLinkVisible);
  };

  const ref = useDetectClickOutside({ onTriggered: () => setPublicLinkVisible(false) });

  // if (isReadOnly) return <></>;

  return (
    <div className={styles.headerWrapper}>
      {currentProject ? (
        <header className={styles.left} id="headerLeft">
          <div
            style={{
              backgroundImage: `url(${
                currentProject?.slug && currentProject?.slug in projectMap
                  ? projectMap[currentProject?.slug].logo
                  : projectMap["lateral"].logo
              })`,
            }}
            className={styles.projectMetaIcon}
          ></div>
          <Link
            to={projectPublicId ? `/public/${projectPublicId}` : `/project/${currentProject?.slug}`}
          >
            {currentProject?.slug && currentProject?.slug in projectMap
              ? projectMap[currentProject?.slug].name
              : currentProject?.slug == "global-graph"
              ? "Global Graph"
              : currentProject?.slug}
          </Link>
        </header>
      ) : (
        <></>
      )}
      {!isReadOnly && (
        <>
          {publicLinkVisible && <PublicLink />}
          {Boolean(projectId) && (
            <header className={styles.right}>
              {!publicLinkVisible && (
                <Tooltip id="public" content="Copy read only link" className="tooltip" />
              )}
              <button
                onClick={togglePublicLink}
                data-tooltip-id="public"
                className={classNames({ [styles.active]: publicLinkVisible })}
              >
                <FontAwesomeIcon icon={faShareNodes} />
              </button>
              <div className={styles.divider}></div>
              <Tooltip id="download" content="Download project" className="tooltip" />
              <button onClick={() => download()} data-tooltip-id="download">
                <FontAwesomeIcon icon={isExporting ? faSpinner : faDownload} spin={isExporting} />
              </button>
              <div className={styles.divider}></div>
              <Tooltip id="logout" content="Log out" className="tooltip" />
              <button onClick={() => logout()} data-tooltip-id="logout">
                <FontAwesomeIcon icon={faSignOut} />
              </button>
              {!isReadOnly && (
                <>
                  <div className={styles.divider}></div>
                  <Tooltip id="user-info-address" content={address} className="tooltip" />
                  <div className={styles.userInfo} data-tooltip-id="user-info-address">
                    {address ? <Blockies seed={address?.toLowerCase()} size={5} /> : <></>}
                  </div>
                </>
              )}
            </header>
          )}
        </>
      )}
    </div>
  );
}
